import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/apiClient";

/**
 * Epson M1100 A4 Service Receipt printable page.
 *
 * Detailed receipt for the customer. Auto-triggers browser print
 * and records the print event.
 */
export default function CaseV2PrintReceiptPage() {
  const { case_id } = useParams();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const recordedRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/cases-v2/${case_id}`);
      setDetail(data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  }, [case_id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (detail && !recordedRef.current) {
      recordedRef.current = true;
      api.post(`/cases-v2/${case_id}/record-print`, { print_type: "receipt" }).catch(() => {});
      const t = setTimeout(() => window.print(), 300);
      return () => clearTimeout(t);
    }
  }, [detail, case_id]);

  if (error) return <div className="p-4 text-rose-700">{error}</div>;
  if (!detail) return <div className="p-4 text-slate-400">Loading…</div>;

  const openedAt = detail.opened_at ? new Date(detail.opened_at).toLocaleString() : "";
  const contents = detail.contents || [];
  const warrantyText =
    detail.warranty_days == null
      ? "As per contract"
      : detail.warranty_days === 0
      ? "Paid Service"
      : `${detail.warranty_days} days · valid till ${detail.warranty_end_date || ""}`;

  return (
    <>
      <style>{`
        @page { size: A4; margin: 12mm; }
        html, body { margin: 0; padding: 0; background: #fff; color: #000; }
        body { font-family: 'Helvetica', 'Arial', sans-serif; font-size: 11pt; line-height: 1.4; }
        .a4 { max-width: 190mm; margin: 0 auto; padding: 0; }
        .a4 h1 { font-size: 22pt; margin: 0; }
        .a4 h2 { font-size: 12pt; margin: 12pt 0 4pt; border-bottom: 1pt solid #000; padding-bottom: 2pt; }
        .a4 .head { display: flex; justify-content: space-between; align-items: flex-start; }
        .a4 .head .r { text-align: right; font-size: 10pt; }
        .a4 table { width: 100%; border-collapse: collapse; margin-top: 4pt; }
        .a4 table th, .a4 table td { border: 0.5pt solid #000; padding: 4pt 6pt; text-align: left; vertical-align: top; font-size: 10pt; word-break: break-word; }
        .a4 .kv { display: grid; grid-template-columns: 130pt 1fr; gap: 3pt 12pt; font-size: 10pt; }
        .a4 .kv .k { font-weight: 700; }
        .a4 .sig { margin-top: 30pt; display: flex; justify-content: space-between; font-size: 10pt; }
        .a4 .sig .box { border-top: 0.5pt solid #000; width: 40%; padding-top: 3pt; text-align: center; }
        .no-print { padding: 12px 16px; }
        @media print { .no-print { display: none; } }
      `}</style>
      <div className="no-print">
        <button onClick={() => window.print()}
          className="rounded-md bg-slate-900 text-white px-3 py-1.5 text-sm">Print again</button>
        <span className="ml-3 text-sm text-slate-500">Epson M1100 · A4 Service Receipt</span>
      </div>

      <div className="a4" data-testid="epson-receipt-print">
        <div className="head">
          <div>
            <h1>Service Receipt</h1>
            <div style={{ fontSize: "10pt", marginTop: "2pt" }}>
              This document acknowledges receipt of the customer’s laptop for service.
            </div>
          </div>
          <div className="r">
            <div style={{ fontSize: "14pt", fontWeight: 800 }}>{detail.case_no}</div>
            <div>Received: {openedAt}</div>
            <div>By: {detail.created_by_label || "—"}</div>
          </div>
        </div>

        <h2>Customer</h2>
        <div className="kv">
          <div className="k">Name</div><div>{detail.customer_name}</div>
          <div className="k">Mobile</div><div>{detail.mobile}{detail.alternate_number ? ` · Alt: ${detail.alternate_number}` : ""}</div>
          <div className="k">Email</div><div>{detail.customer_email || "—"}</div>
          <div className="k">Company</div><div>{detail.company_name || "—"}{detail.gst_number ? ` · GST ${detail.gst_number}` : ""}</div>
          <div className="k">Address</div>
          <div>{[detail.address_line, detail.city, detail.state, detail.pincode].filter(Boolean).join(", ") || "—"}</div>
          <div className="k">Source</div><div>{(detail.customer_source || "").replaceAll("_", " ")}</div>
        </div>

        <h2>Laptop</h2>
        <div className="kv">
          <div className="k">Brand / Model</div><div>{[detail.brand, detail.model].filter(Boolean).join(" ") || "—"}</div>
          <div className="k">Manufacturer Serial</div><div>{detail.manufacturer_serial || (detail.laptop_power_status === "dead" ? "N/A (laptop dead)" : "—")}</div>
          <div className="k">Specs</div><div>{[detail.processor, detail.ram, detail.storage, detail.generation].filter(Boolean).join(" · ") || "—"}</div>
          <div className="k">Power Status</div><div>{detail.laptop_power_status || "—"}</div>
          <div className="k">Charger Included</div><div>{detail.charger_included ? "Yes" : "No"}</div>
          <div className="k">Overall Condition</div><div>{(detail.overall_condition || "—").toUpperCase()}{detail.condition_remarks ? ` — ${detail.condition_remarks}` : ""}</div>
        </div>

        <h2>Warranty</h2>
        <div className="kv">
          <div className="k">Status</div><div>{warrantyText}</div>
        </div>

        <h2>Complaint</h2>
        <div style={{ whiteSpace: "pre-wrap" }}>{detail.complaint}</div>

        <h2>Contents Received</h2>
        <table>
          <thead>
            <tr><th style={{ width: "24pt" }}>#</th><th>Item</th><th style={{ width: "40pt" }}>Qty</th><th>Remarks</th></tr>
          </thead>
          <tbody>
            {contents.length === 0 && <tr><td colSpan={4} style={{ textAlign: "center" }}>—</td></tr>}
            {contents.map((c, i) => (
              <tr key={c.id || i}>
                <td>{i + 1}</td>
                <td>{c.item_type === "other" ? (c.custom_name || "other") : c.item_type.replaceAll("_", " ")}</td>
                <td>{c.quantity}</td>
                <td>{c.remarks || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ marginTop: "10pt", fontSize: "9pt" }}>
          <b>Note:</b> All estimates are provisional. Data on the device is customer’s responsibility;
          service centre is not liable for accidental data loss during repairs.
        </div>

        <div className="sig">
          <div className="box">Customer Signature</div>
          <div className="box">Service Centre</div>
        </div>
      </div>
    </>
  );
}
